import { DashboardStatValue } from 'components/animations';
import { useReducedMotion } from 'animations/useReducedMotion';
import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import { getDashboardAnalyticsThunk } from '@redux/features/analytics.slice';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
} from 'recharts';
import {
    DollarSign,
    ShoppingCart,
    Users,
    Package,
    TrendingUp,
    Clock,
    AlertCircle,
    CheckCircle,
    TrendingDown,
    Award,
} from 'lucide-react';

const Dashboard = () => {
    const dispatch = useAppDispatch();
    const { dashboardAnalytics, dashboardLoading } = useAppSelector((state: RootState) => state.analyticsState);
    const reducedMotion = useReducedMotion();
    const mainMetricsRef = useRef<HTMLDivElement>(null);
    const orderStatusRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        dispatch(getDashboardAnalyticsThunk());
    }, [dispatch]);

    useEffect(() => {
        if (dashboardLoading || reducedMotion) {
            return;
        }
        let cancelled = false;
        const run = async () => {
            const { gsap } = await import('gsap');
            if (cancelled) {
                return;
            }
            const cards = [
                ...(mainMetricsRef.current?.querySelectorAll('.dashboard-metric-card') ?? []),
                ...(orderStatusRef.current?.querySelectorAll('.dashboard-metric-card') ?? []),
            ];
            if (cards.length > 0) {
                gsap.from(cards, {
                    opacity: 0,
                    y: 20,
                    duration: 0.45,
                    stagger: 0.06,
                    ease: 'power2.out',
                });
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [dashboardLoading, reducedMotion]);

    // Extract data - handle both nested and direct structure
    const analyticsData =
        dashboardAnalytics && typeof dashboardAnalytics === 'object' && 'data' in dashboardAnalytics
            ? (dashboardAnalytics as any).data
            : dashboardAnalytics;

    const data = analyticsData || {
        totalRevenue: 0,
        todayRevenue: 0,
        averageOrderValue: 0,
        totalOrders: 0,
        todayOrders: 0,
        totalUsers: 0,
        newUsersToday: 0,
        activeUsers: 0,
        totalProducts: 0,
        lowStockProducts: 0,
        outOfStockProducts: 0,
        pendingOrders: 0,
        processingOrders: 0,
        deliveredOrders: 0,
        orderFulfillmentRate: 0,
        revenueTrends: [],
        userGrowthTrends: [],
        ordersByStatus: [],
        peakOrderingTimes: [],
        batchAnalytics: null,
        recommendedProducts: [],
    };

    // Format currency
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(value);
    };

    // Format date for display
    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } catch {
            return dateString;
        }
    };

    // Prepare data for charts - updated to match new response structure
    const revenueChartData =
        Array.isArray(data.revenueTrends) && data.revenueTrends.length > 0
            ? data.revenueTrends.map((item: any) => ({
                  date: formatDate(item.date),
                  revenue: item.value || 0,
                  fullDate: item.date,
              }))
            : [];

    const userGrowthData =
        Array.isArray(data.userGrowthTrends) && data.userGrowthTrends.length > 0
            ? data.userGrowthTrends.map((item: any) => ({
                  date: formatDate(item.date),
                  users: item.value || 0,
                  fullDate: item.date,
              }))
            : [];

    const ordersByStatusData =
        Array.isArray(data.ordersByStatus) && data.ordersByStatus.length > 0
            ? data.ordersByStatus.map((item: any) => ({
                  status: item.status?.replace(/_/g, ' ') || 'Unknown',
                  count: item.count || 0,
                  percentage: item.percentage || 0,
              }))
            : [];

    const peakOrderingData =
        Array.isArray(data.peakOrderingTimes) && data.peakOrderingTimes.length > 0
            ? data.peakOrderingTimes
                  .map((item: any) => ({
                      hour: `${item.hour}:00`,
                      hourNum: item.hour,
                      orders: item.orderCount || 0,
                  }))
                  .sort((a: any, b: any) => a.hourNum - b.hourNum)
            : [];

    // Batch Analytics Data
    const batchAnalytics = data.batchAnalytics || null;
    const batchProfitsData = batchAnalytics?.batchProfits || [];
    const recommendedProductsData = Array.isArray(data.recommendedProducts) ? data.recommendedProducts : [];

    // Prepare batch profits chart data
    const batchProfitsChartData = batchProfitsData.map((batch: any) => ({
        name: batch.productName || `Batch ${batch.batchId}`,
        profit: batch.netProfit || 0,
        revenue: batch.revenue || 0,
        cost: batch.batchCost || 0,
        margin: batch.profitMargin || 0,
    }));

    // Prepare recommended products chart data
    const recommendedProductsChartData = recommendedProductsData.map((product: any) => ({
        name: product.productName || `Product ${product.productId}`,
        revenue: product.totalRevenue || 0,
        orders: product.totalOrders || 0,
        quantity: product.totalQuantitySold || 0,
        avgOrderValue: product.averageOrderValue || 0,
    }));

    // Colors for charts
    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

    // Metric Cards
    const metricCards = [
        {
            title: 'Total Revenue',
            value: formatCurrency(data.totalRevenue || 0),
            animatedValue: data.totalRevenue || 0,
            valueFormat: formatCurrency,
            subtitle: `Today: ${formatCurrency(data.todayRevenue || 0)}`,
            icon: DollarSign,
            color: 'text-green-500',
            bgColor: 'bg-green-100 dark:bg-green-400/20',
            borderColor: 'border-green-200 dark:border-green-600',
        },
        {
            title: 'Total Orders',
            value: (data.totalOrders ?? 0).toString(),
            animatedValue: data.totalOrders ?? 0,
            subtitle: `Today: ${data.todayOrders ?? 0} orders`,
            icon: ShoppingCart,
            color: 'text-blue-500',
            bgColor: 'bg-blue-100 dark:bg-blue-400/20',
            borderColor: 'border-blue-200 dark:border-blue-600',
        },
        {
            title: 'Total Users',
            value: (data.totalUsers ?? 0).toString(),
            animatedValue: data.totalUsers ?? 0,
            subtitle: `Active: ${data.activeUsers ?? 0} users`,
            icon: Users,
            color: 'text-purple-500',
            bgColor: 'bg-purple-100 dark:bg-purple-400/20',
            borderColor: 'border-purple-200 dark:border-purple-600',
        },
        {
            title: 'Total Products',
            value: (data.totalProducts ?? 0).toString(),
            animatedValue: data.totalProducts ?? 0,
            subtitle: `Low Stock: ${data.lowStockProducts ?? 0}, Out: ${data.outOfStockProducts ?? 0}`,
            icon: Package,
            color: 'text-orange-500',
            bgColor: 'bg-orange-100 dark:bg-orange-400/20',
            borderColor: 'border-orange-200 dark:border-orange-600',
        },
    ];

    const orderStatusCards = [
        {
            title: 'Pending Orders',
            value: (data.pendingOrders ?? 0).toString(),
            icon: Clock,
            color: 'text-yellow-500',
            bgColor: 'bg-yellow-100 dark:bg-yellow-400/20',
            borderColor: 'border-yellow-200 dark:border-yellow-600',
        },
        {
            title: 'Processing Orders',
            value: (data.processingOrders ?? 0).toString(),
            icon: TrendingUp,
            color: 'text-blue-500',
            bgColor: 'bg-blue-100 dark:bg-blue-400/20',
            borderColor: 'border-blue-200 dark:border-blue-600',
        },
        {
            title: 'Delivered Orders',
            value: (data.deliveredOrders ?? 0).toString(),
            icon: CheckCircle,
            color: 'text-green-500',
            bgColor: 'bg-green-100 dark:bg-green-400/20',
            borderColor: 'border-green-200 dark:border-green-600',
        },
        {
            title: 'Fulfillment Rate',
            value: `${(data.orderFulfillmentRate ?? 0).toFixed(1)}%`,
            icon: AlertCircle,
            color: 'text-indigo-500',
            bgColor: 'bg-indigo-100 dark:bg-indigo-400/20',
            borderColor: 'border-indigo-200 dark:border-indigo-600',
        },
    ];

    if (dashboardLoading) {
        return (
            <>
                <BreadCrumb pageTitle="Dashboards" />
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-custom-500"></div>
                        <p className="mt-4 text-slate-500">Loading dashboard data...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <BreadCrumb pageTitle="Dashboards" />
            <div className="container-fluid mx-auto group-data-[content=boxed]:max-w-boxed">
                {/* Main Metrics Cards */}
                <div ref={mainMetricsRef} className="grid grid-cols-1 gap-5 mb-5 md:grid-cols-2 xl:grid-cols-4">
                    {metricCards.map((card, index) => {
                        const IconComponent = card.icon;
                        return (
                            <div
                                key={index}
                                className={`dashboard-metric-card rounded-lg shadow p-4 ${card.bgColor} ${card.borderColor} border`}
                            >
                                <div className="card-body">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="mb-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                                                {card.title}
                                            </p>
                                            <h3 className="mb-1 text-2xl font-semibold text-slate-800 dark:text-slate-200">
                                                {'animatedValue' in card && card.animatedValue !== undefined ? (
                                                    <DashboardStatValue
                                                        value={card.animatedValue as number}
                                                        format={
                                                            'valueFormat' in card && card.valueFormat
                                                                ? (card.valueFormat as (n: number) => string)
                                                                : undefined
                                                        }
                                                    />
                                                ) : (
                                                    card.value
                                                )}
                                            </h3>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                {card.subtitle}
                                            </p>
                                        </div>
                                        <div className={`p-3 rounded-full ${card.bgColor}`}>
                                            <IconComponent className={`size-6 ${card.color}`} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Order Status Cards */}
                <div ref={orderStatusRef} className="grid grid-cols-1 gap-5 mb-5 md:grid-cols-2 xl:grid-cols-4">
                    {orderStatusCards.map((card, index) => {
                        const IconComponent = card.icon;
                        return (
                            <div
                                key={index}
                                className={`dashboard-metric-card rounded-lg shadow p-4 ${card.bgColor} ${card.borderColor} border`}
                            >
                                <div className="">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="mb-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                                                {card.title}
                                            </p>
                                            <h3 className="text-2xl font-semibold text-slate-800 dark:text-slate-200">
                                                {card.value}
                                            </h3>
                                        </div>
                                        <div className={`p-3 rounded-full ${card.bgColor}`}>
                                            <IconComponent className={`size-6 ${card.color}`} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 gap-5 mb-5 lg:grid-cols-2">
                    {/* Revenue Trends Chart */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="">
                            <h6 className="mb-4 text-15 font-semibold text-slate-800 dark:text-slate-200">
                                Revenue Trends
                            </h6>
                            {revenueChartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={revenueChartData}>
                                        <defs>
                                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 11, fill: '#60a5fa' }}
                                            stroke="#60a5fa"
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: '#60a5fa' }}
                                            stroke="#60a5fa"
                                            tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                                        />
                                        <Tooltip
                                            formatter={(value: number) => formatCurrency(value)}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                border: '1px solid #60a5fa',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 6px rgba(96, 165, 250, 0.2)',
                                            }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="revenue"
                                            stroke="#3b82f6"
                                            strokeWidth={2}
                                            fillOpacity={1}
                                            fill="url(#colorRevenue)"
                                            name="Revenue"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-[300px] text-slate-400">
                                    No revenue trend data available
                                </div>
                            )}
                        </div>
                    </div>

                    {/* User Growth Trends Chart */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <div>
                            <h6 className="mb-4 text-15 font-semibold text-slate-800 dark:text-slate-200">
                                User Growth Trends
                            </h6>
                            {userGrowthData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={userGrowthData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 11, fill: '#34d399' }}
                                            stroke="#34d399"
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                        />
                                        <YAxis tick={{ fontSize: 11, fill: '#34d399' }} stroke="#34d399" />
                                        <Tooltip
                                            formatter={(value: number) => [`${value} users`, 'New Users']}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                border: '1px solid #34d399',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 6px rgba(52, 211, 153, 0.2)',
                                            }}
                                        />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="users"
                                            stroke="#10b981"
                                            strokeWidth={2}
                                            dot={{ fill: '#10b981', r: 4 }}
                                            activeDot={{ r: 6 }}
                                            name="New Users"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-[300px] text-slate-400">
                                    No user growth data available
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Orders by Status Chart */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <div>
                            <h6 className="mb-4 text-15 font-semibold text-slate-800 dark:text-slate-200">
                                Orders by Status
                            </h6>
                            {ordersByStatusData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={ordersByStatusData} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis
                                            type="number"
                                            tick={{ fontSize: 11, fill: '#818cf8' }}
                                            stroke="#818cf8"
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="status"
                                            tick={{ fontSize: 10, fill: '#818cf8' }}
                                            stroke="#818cf8"
                                            width={120}
                                        />
                                        <Tooltip
                                            formatter={(value: number, name: string, props: any) => [
                                                `${value} orders (${props.payload.percentage || 0}%)`,
                                                'Count',
                                            ]}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                border: '1px solid #818cf8',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 6px rgba(129, 140, 248, 0.2)',
                                            }}
                                        />
                                        <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Orders" />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-[300px] text-slate-400">
                                    No order status data available
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Peak Ordering Times Chart */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <div>
                            <h6 className="mb-4 text-15 font-semibold text-slate-800 dark:text-slate-200">
                                Peak Ordering Times
                            </h6>
                            {peakOrderingData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={peakOrderingData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis
                                            dataKey="hour"
                                            tick={{ fontSize: 11, fill: '#f59e0b' }}
                                            stroke="#f59e0b"
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                        />
                                        <YAxis tick={{ fontSize: 11, fill: '#f59e0b' }} stroke="#f59e0b" />
                                        <Tooltip
                                            formatter={(value: number) => [`${value} orders`, 'Order Count']}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                border: '1px solid #f59e0b',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 6px rgba(245, 158, 11, 0.2)',
                                            }}
                                        />
                                        <Bar dataKey="orders" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Orders" />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-[300px] text-slate-400">
                                    No peak ordering time data available
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Batch Profits Chart */}
                    {batchProfitsChartData.length > 0 && (
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="card-body">
                                <h6 className="mb-4 text-15 font-semibold text-slate-800 dark:text-slate-200">
                                    Batch Profit Analysis
                                </h6>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={batchProfitsChartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 10, fill: '#10b981' }}
                                            stroke="#10b981"
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: '#10b981' }}
                                            stroke="#10b981"
                                            tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                                        />
                                        <Tooltip
                                            formatter={(value: number, name: string) => {
                                                if (name === 'profit') return [formatCurrency(value), 'Net Profit'];
                                                if (name === 'revenue') return [formatCurrency(value), 'Revenue'];
                                                if (name === 'cost') return [formatCurrency(value), 'Cost'];
                                                return [value, name];
                                            }}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                border: '1px solid #10b981',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)',
                                            }}
                                        />
                                        <Legend />
                                        <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
                                        <Bar dataKey="cost" fill="#ef4444" name="Cost" />
                                        <Bar dataKey="profit" fill="#3b82f6" name="Net Profit" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* Recommended Products Chart */}
                    {recommendedProductsChartData.length > 0 && (
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="card-body">
                                <h6 className="mb-4 text-15 font-semibold text-slate-800 dark:text-slate-200">
                                    Recommended Products Performance
                                </h6>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={recommendedProductsChartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 10, fill: '#a78bfa' }}
                                            stroke="#a78bfa"
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: '#a78bfa' }}
                                            stroke="#a78bfa"
                                            yAxisId="left"
                                            tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: '#f472b6' }}
                                            stroke="#f472b6"
                                            yAxisId="right"
                                            orientation="right"
                                        />
                                        <Tooltip
                                            formatter={(value: number, name: string) => {
                                                if (name === 'revenue') return [formatCurrency(value), 'Revenue'];
                                                if (name === 'orders') return [`${value} orders`, 'Total Orders'];
                                                if (name === 'quantity') return [`${value} units`, 'Quantity Sold'];
                                                return [value, name];
                                            }}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                                border: '1px solid #a78bfa',
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 6px rgba(167, 139, 250, 0.2)',
                                            }}
                                        />
                                        <Legend />
                                        <Bar yAxisId="left" dataKey="revenue" fill="#8b5cf6" name="Revenue" />
                                        <Bar yAxisId="right" dataKey="orders" fill="#ec4899" name="Orders" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </div>

                {/* Batch Analytics Summary Cards */}
                {batchAnalytics && (
                    <div className="grid grid-cols-1 gap-5 mb-5 md:grid-cols-2 xl:grid-cols-4">
                        <div className="card bg-green-50 dark:bg-green-400/10 border-green-200 dark:border-green-600 border">
                            <div className="card-body">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="mb-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                                            Total Batch Revenue
                                        </p>
                                        <h3 className="text-2xl font-semibold text-slate-800 dark:text-slate-200">
                                            {formatCurrency(batchAnalytics.totalBatchRevenue || 0)}
                                        </h3>
                                    </div>
                                    <div className="p-3 rounded-full bg-green-100 dark:bg-green-400/20">
                                        <DollarSign className="size-6 text-green-500" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card bg-blue-50 dark:bg-blue-400/10 border-blue-200 dark:border-blue-600 border">
                            <div className="card-body">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="mb-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                                            Total Net Profit
                                        </p>
                                        <h3 className="text-2xl font-semibold text-slate-800 dark:text-slate-200">
                                            {formatCurrency(batchAnalytics.totalNetProfit || 0)}
                                        </h3>
                                    </div>
                                    <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-400/20">
                                        <TrendingUp className="size-6 text-blue-500" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card bg-purple-50 dark:bg-purple-400/10 border-purple-200 dark:border-purple-600 border">
                            <div className="card-body">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="mb-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                                            Profit Margin
                                        </p>
                                        <h3 className="text-2xl font-semibold text-slate-800 dark:text-slate-200">
                                            {batchAnalytics.overallProfitMargin?.toFixed(1) || 0}%
                                        </h3>
                                    </div>
                                    <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-400/20">
                                        <Award className="size-6 text-purple-500" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card bg-orange-50 dark:bg-orange-400/10 border-orange-200 dark:border-orange-600 border">
                            <div className="card-body">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="mb-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                                            Active Batches
                                        </p>
                                        <h3 className="text-2xl font-semibold text-slate-800 dark:text-slate-200">
                                            {batchAnalytics.activeBatches || 0}
                                        </h3>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            Total: {batchAnalytics.totalBatches || 0}
                                        </p>
                                    </div>
                                    <div className="p-3 rounded-full bg-orange-100 dark:bg-orange-400/20">
                                        <Package className="size-6 text-orange-500" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Best Batch & Additional Info */}
                <div className="grid grid-cols-1 gap-5 mb-5 lg:grid-cols-3">
                    {/* Best Batch Card */}
                    {batchAnalytics?.bestBatch && (
                        <div className="bg-white rounded-lg shadow p-4">
                            <div>
                                <h6 className="mb-4 text-15 font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                    <Award className="size-5 text-yellow-500" />
                                    Best Performing Batch
                                </h6>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Product</p>
                                        <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                                            {batchAnalytics.bestBatch.productName}
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Net Profit</p>
                                            <p className="text-lg font-bold text-green-600">
                                                {formatCurrency(batchAnalytics.bestBatch.netProfit)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Profit Margin</p>
                                            <p className="text-lg font-bold text-purple-600">
                                                {batchAnalytics.bestBatch.profitMargin.toFixed(1)}%
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Revenue</p>
                                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                                {formatCurrency(batchAnalytics.bestBatch.revenue)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Quantity Sold</p>
                                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                                {batchAnalytics.bestBatch.quantitySold} /{' '}
                                                {batchAnalytics.bestBatch.totalQuantity}
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Lot Number</p>
                                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                            {batchAnalytics.bestBatch.lotNumber}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Average Order Value */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <div>
                            <h6 className="mb-4 text-15 font-semibold text-slate-800 dark:text-slate-200">
                                Average Order Value
                            </h6>
                            <div className="flex items-center justify-center h-[200px]">
                                <div className="text-center">
                                    <p className="text-4xl font-bold text-custom-500">
                                        {formatCurrency(data.averageOrderValue || 0)}
                                    </p>
                                    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Per order</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* New Users Today */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <div>
                            <h6 className="mb-4 text-15 font-semibold text-slate-800 dark:text-slate-200">
                                New Users Today
                            </h6>
                            <div className="flex items-center justify-center h-[200px]">
                                <div className="text-center">
                                    <p className="text-4xl font-bold text-purple-500">{data.newUsersToday ?? 0}</p>
                                    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">New registrations</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Dashboard;
